import { Icons } from '@/components/icons';
import type { ISubMenu } from '@/components/sidebar/data';
import { isFeatureEnabled } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { Urls } from '@/lib/urls';

import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  BellIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CheckIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CubeIcon,
  CubeTransparentIcon,
  DocumentArrowUpIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  HomeModernIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  ListBulletIcon,
  LockOpenIcon,
  PaperAirplaneIcon,
  PuzzlePieceIcon,
  ReceiptPercentIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  Squares2X2Icon,
  SquaresPlusIcon,
  TagIcon,
  UserIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/16/solid';
import {
  ApartmentTermsConfig,
  DimensionConfigurationConfig,
  type ModelName,
} from '@pigello/pigello-matrix';
import {
  CogIcon,
  EarthIcon,
  FileIcon,
  KeyIcon,
  MonitorSmartphone,
  PaintbrushIcon,
  UserLockIcon,
  Wrench,
} from 'lucide-react';

type GroupType =
  | 'admin'
  | 'globalconfiguration'
  | 'security'
  | 'modulesettings';

interface IMenuGroup {
  title: string;
  isIndex?: boolean;
  key: string;
  subMenus: ISubMenu[];
}

export type SettingsMenu = Record<GroupType, IMenuGroup>;

function getSubItemsModelNames<T extends Array<ISubMenu>>(
  subItems: T,
  // Only pick out modelname from objects where the field is of type ModelName,
  // then we index into those to get a union type of all model names used
  skipModels?: Array<
    | Extract<T[number], { modelNames: ModelName[] }>['modelNames'][number]
    | Extract<T[number], { modelName: ModelName }>['modelName']
  >
): ModelName[] {
  const summedModelNames: ModelName[] = [];

  const checkModelName = (modelName: ModelName | undefined) => {
    if (!modelName) return;

    if (skipModels && skipModels.includes(modelName)) return;

    summedModelNames.push(modelName);
  };

  for (const item of subItems) {
    checkModelName(item.modelName);

    if (item.modelNames)
      for (const modelName of item.modelNames) {
        checkModelName(modelName);
      }
  }

  return summedModelNames;
}

export const contractCancellationsRouteData: ISubMenu[] = [
  {
    title: 'Inställningar',
    key: 'settings',
    path: Urls.settings.modules.base.contractcancellations.settings,
    icon: <Cog6ToothIcon className='mr-2 size-4' />,
  },
  {
    title: 'Anledningar',
    key: 'reasons',
    path: Urls.settings.modules.base.contractcancellations.reasons,
    icon: <ListBulletIcon className='mr-2 size-4' />,
  },
];

export const accountingRouteData: ISubMenu[] = [
  {
    title: 'Generella inställningar',
    key: 'general',
    path: Urls.settings.modules.invoicing.accounting.general,
    icon: <Cog6ToothIcon className='mr-2 size-4' />,
  },
  {
    title: 'Bokföringsmetod',
    key: 'method',
    path: Urls.settings.modules.invoicing.accounting.method,
    icon: <AdjustmentsHorizontalIcon className='mr-2 size-4' />,
  },
  {
    title: 'Kontoplan',
    key: 'accounts',
    path: Urls.settings.modules.invoicing.accounting.accounts,
    icon: <ArchiveBoxIcon className='mr-2 size-4' />,
  },
  {
    title: 'Verifikat',
    key: 'verifications',
    path: Urls.settings.modules.invoicing.accounting.verifications,
    icon: <BookOpenIcon className='mr-2 size-4' />,
  },
  {
    title: 'Betalmetoder',
    key: 'payment-methods',
    path: Urls.settings.modules.invoicing.accounting.paymentmethods,
    icon: <BanknotesIcon className='mr-2 size-4' />,
  },
  {
    title: 'Dimensioner',
    key: 'dimensions',
    path: Urls.settings.modules.invoicing.accounting.dimensions,
    icon: <TagIcon className='mr-2 size-4' />,
    modelName: DimensionConfigurationConfig.modelName,
  },
];

export const articlesRouteData: ISubMenu[] = [
  {
    title: 'Hyresartiklar',
    key: 'rent',
    path: Urls.settings.modules.invoicing.articles.rent,
    icon: <DocumentCheckIcon className='mr-2 size-4' />,
  },
  {
    title: 'Vakansartiklar',
    key: 'vacancy',
    path: Urls.settings.modules.invoicing.articles.vacancy,
    icon: <CubeTransparentIcon className='mr-2 size-4' />,
  },
  {
    title: 'Faktureringsartiklar',
    key: 'invoice',
    path: Urls.settings.modules.invoicing.articles.invoice,
    icon: <ReceiptPercentIcon className='mr-2 size-4' />,
  },
  {
    title: 'Förbrukningsartiklar',
    key: 'consumable',
    path: Urls.settings.modules.invoicing.articles.consumable,
    icon: <ShoppingBagIcon className='mr-2 size-4' />,
  },
  {
    title: 'Artikelöverskrivningar',
    key: 'overridden',
    path: Urls.settings.modules.invoicing.articles.overriddenArticles,
    icon: <InformationCircleIcon className='mr-2 size-4' />,
  },
  {
    title: 'Kategorier',
    key: 'categories',
    path: Urls.settings.modules.invoicing.articles.categories,
    icon: <TagIcon className='mr-2 size-4' />,
  },
];

export const invoicingSettingsRouteData: ISubMenu[] = [
  {
    title: 'Avtalsavisering',
    key: 'contractinvoicing',
    path: Urls.settings.modules.invoicing.invoicingsettings.contractinvoicing,
    icon: <DocumentArrowUpIcon className='mr-2 size-4' />,
  },
  {
    title: 'Betalningsvillkor',
    key: 'paymentterms',
    path: Urls.settings.modules.invoicing.invoicingsettings.paymentterms,
    icon: <CreditCardIcon className='mr-2 size-4' />,
  },
];

export const profileSettingsRouteData: ISubMenu[] = [
  {
    title: 'Användarinformation',
    key: 'settings',
    path: Urls.settings.profile.settings,
    icon: <UserIcon className='mr-2 size-4' />,
    modelName: 'organizationuser',
  },
  {
    title: 'Segment',
    key: 'segments',
    path: '/settings/profile/segments',
    icon: <Squares2X2Icon className='mr-2 size-4' />,
    modelName: 'segment',
  },
  {
    title: 'Mina arbetsroller',
    key: 'work-engagements',
    path: '/settings/profile/work-engagements',
    icon: <BriefcaseIcon className='mr-2 size-4' />,
    modelNames: ['workengagement', 'organizationuser'],
  },

  {
    title: 'Notifikationsinställningar',
    key: 'notification-settings',
    path: '/settings/profile/notification-settings',
    icon: <BellIcon className='mr-2 size-4' />,
  },
  {
    title: 'Sessioner',
    key: 'sessions',
    path: '/settings/profile/sessions',
    icon: <MonitorSmartphone className='mr-2 size-4' />,
  },
];

export const marketRequirementProfilesData: ISubMenu[] = [
  {
    title: 'Lägenheter',
    key: 'apartment',
    icon: <HomeModernIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.market.requirementProfiles.apartment.overview,
    modelName: 'apartmentrequirementprofile',
  },
  {
    title: 'Globala inställningar',
    key: 'global-requirement-settings',
    icon: <Cog6ToothIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.market.requirementProfiles.globals.overview,
    modelName: 'apartmentresidentrequirement',
  },
];

export const rentableTermsData: ISubMenu[] = [
  {
    title: 'Lägenheter',
    key: 'apartment',
    icon: <HomeModernIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.market.terms.apartment.overview,
    modelName: 'apartmentterms',
  },
];

export const queuesSidebarData: ISubMenu[] = [
  {
    title: 'Lägenhetsköer',
    key: 'apartment-queues',
    icon: <ListBulletIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.market.queues.apartmentQueues,
    modelName: 'apartmentqueue',
  },
  {
    title: 'Köregler',
    key: 'queue-rules',
    icon: <CogIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.market.queues.queueRules,
    modelName: 'queuerules',
  },
];

export const adminSettingsRouteData: ISubMenu[] = [
  {
    title: 'Kunder',
    key: 'customers',
    path: Urls.settings.admin.customers,
    icon: <BuildingOffice2Icon className='mr-2 size-4' />,
  },
];

const adminSubMenuItems = [
  {
    path: Urls.settings.organization.users.default,
    key: 'users',
    modelName: 'organizationuser',
    detailModalModelName: 'organizationuser',
    title: 'Användare',
    icon: <UsersIcon className='mr-2 size-4' />,
  },
  {
    path: Urls.settings.organization.supportAccounts.default,
    key: 'support-accounts',
    modelName: 'organizationuser',
    detailModalModelName: 'organizationuser',
    title: 'Supportanvändare',
    icon: <LifebuoyIcon className='mr-2 size-4' />,
  },
  {
    title: 'Behörighetsgrupper',
    icon: <LockOpenIcon className='mr-2 size-4' />,
    path: Urls.settings.organization.roles.default,
    key: 'roles',
    modelName: 'role',
  },
  {
    title: 'Arbetsroller',
    icon: <BriefcaseIcon className='mr-2 size-4' />,
    path: Urls.settings.organization.workengagements.default,
    key: 'workengagements',
    modelName: 'workengagement',
  },
  {
    title: 'Kundhantering (Admin)',
    icon: <EarthIcon className='mr-2 size-4' />,
    path: Urls.settings.admin.customers,
    key: 'admin-customers',
    pigelloAdminOnly: true,
  },
  {
    title: 'Systemadministratörer',
    icon: <Wrench className='mr-2 size-4' />,
    path: Urls.settings.organization.systemManagers.default,
    key: 'system-managers',
    systemManagerOnly: true,
  },
] as const satisfies ISubMenu[];

const globalConfigurationsSubMenuItems = [
  {
    title: 'Monitorering',
    icon: <SquaresPlusIcon className='mr-2 size-4' />,
    path: Urls.settings.organization.monitoringconfigurations.default,
    key: 'monitoringconfigurations',
    modelName: 'monitoringconfiguration',
  },
  {
    path: Urls.settings.organization.validationconfig.default,
    title: 'Valideringar',
    icon: <CheckIcon className='mr-2 size-4' />,
    key: 'validation-config',
    modelName: 'validationconfiguration',
  },
  {
    path: Urls.settings.organization.branding.default,
    title: 'Branding',
    icon: <PaintbrushIcon className='mr-2 size-4' />,
    key: 'branding',
    systemManagerOnly: true,
  },
  {
    path: Urls.settings.organization.bankid.default,
    title: 'BankID',
    icon: <UserLockIcon className='mr-2 size-4' />,
    key: 'bankid',
    systemManagerOnly: true,
    pigelloAdminOnly: true,
  },
  {
    path: Urls.settings.organization.ssoConfiguration.default,
    title: 'SSO',
    icon: <KeyIcon className='mr-2 size-4' />,
    key: 'sso-configuration',
    systemManagerOnly: true,
  },
  {
    path: Urls.settings.organization.tags.default,
    title: 'Taggar',
    icon: <TagIcon className='mr-2 size-4' />,
    key: 'tags',
    modelName: 'tag',
  },
  {
    path: Urls.settings.organization.customercolumntype.default,
    title: 'Anpassade fält',
    icon: <CubeTransparentIcon className='mr-2 size-4' />,
    modelName: 'customercolumntype',
    key: 'customercolumns',
  },
  {
    path: Urls.settings.organization.organizationconfig.default,
    title: 'Organisationer',
    icon: <BuildingOffice2Icon className='mr-2 size-4' />,
    key: 'organization-config',
    modelName: 'organization',
  },
  {
    title: 'Integrationer',
    icon: <PuzzlePieceIcon className='mr-2 size-4' />,
    path: Urls.settings.organization.integrations,
    key: 'allintegrations',
    modelName: 'integrationuseragent',
  },
] as const satisfies ISubMenu[];

const securitySubMenuItems = [
  {
    path: Urls.settings.organization.securityconfig.default,
    title: 'Inloggning',
    icon: <ShieldCheckIcon className='mr-2 size-4' />,
    key: 'security-config',
    modelName: 'securityconfiguration',
  },
] as const satisfies ISubMenu[];

const baseSubMenuItems = [
  {
    title: 'Avtalsuppsägning',
    key: 'contractcancellations',
    path: Urls.settings.modules.base.contractcancellations.settings,
    modelName: 'contractcancellationconfiguration',
  },
  {
    title: 'Svenskt prisbasbelopp',
    key: 'swedishpricebasevalue',
    path: Urls.settings.modules.base.swedishPriceBase,
    modelName: 'swedishpricebasevalue',
  },
  {
    title: 'Referensränta',
    key: 'swedishreferencerate',
    path: Urls.settings.modules.base.swedishReferenceRate,
    modelName: 'swedishreferencerate',
  },
] as const satisfies ISubMenu[];

export const chatgroupConfigurationItems = [
  {
    title: 'Inställningar',
    key: 'chatgroup-configuration',
    icon: <Cog6ToothIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.tenantportal.chat.chatgroupconfiguration,
  },
] as const satisfies ISubMenu[];

const errandsSubMenuItems = [
  {
    title: 'Komponentinställningar',
    key: 'components',
    path: Urls.settings.modules.errands.componentsettings.componenttypes,
    modelNames: ['componenttype', 'componentmodel', 'roomcategory'],
  },
  {
    title: 'Ärendehantering',
    key: 'errandmanagement',
    modelName: 'generalerrandtype',
    path: Urls.settings.modules.errands.general.types,
  },
  {
    title: 'Besiktningsinställningar',
    key: 'inspections-settings',
    path: Urls.settings.modules.errands.inspection.types,
    modelName: 'inspectionerrandsetting',
  },
  {
    title: 'Externa leverantörer',
    key: 'external',
    modelName: 'externalsupplier',
    path: Urls.settings.modules.errands.external.suppliers,
  },
  {
    title: 'Standarduppsättningar',
    key: 'default',
    modelNames: ['generalerranddefaultdescription', 'generalerrandaction'],
    path: Urls.settings.modules.errands.default.descriptions,
  },
] as const satisfies ISubMenu[];

const invoicingSubMenuItems = [
  {
    title: 'Bokföring',
    key: 'bookkeeping',
    path: Urls.settings.modules.invoicing.accounting.accounts,
    modelName: 'bookkeepingaccountsconfig',
  },
  {
    title: 'Avtalsavisering',
    key: 'contractinvoicing',
    path: Urls.settings.modules.invoicing.invoicingsettings.contractinvoicing,
    modelNames: ['automaticcontractinvoicingsetting', 'paymenttermssetting'],
  },
  {
    title: 'Artiklar',
    key: 'articles',
    path: Urls.settings.modules.invoicing.articles.rent,
    modelNames: ['article', 'articlecategory'],
  },
  {
    title: 'Vakansbokning',
    key: 'vacancies',
    path: Urls.settings.modules.invoicing.vacancies,
    modelNames: ['automaticvacancyinvoicingsetting'],
  },
  {
    title: 'Avi-utseende',
    key: 'pdfconfig',
    path: Urls.settings.modules.invoicing.pdfconfig,
    modelName: 'invoicerelatedpdfconfiguration',
  },
  {
    path: Urls.settings.organization.retroactivityconfig.default,
    title: 'Retroaktivitet',
    icon: <RectangleGroupIcon className='mr-2 size-4' />,
    key: 'retroactivity-config',
    modelName: 'retroactivityconfiguration',
  },
  {
    title: 'Meddelanden och bilagor',
    key: 'messages',
    path: Urls.settings.modules.invoicing.contractinvoicingmessages,
    icon: <EnvelopeIcon className='mr-2 size-4' />,
  },
  {
    title: 'Betalfiler',
    key: 'payment-files',
    path: Urls.settings.modules.invoicing.accounting.paymentfiles,
    icon: <FileIcon className='mr-2 size-4' />,
  },
] as const satisfies ISubMenu[];

export const communicationSubMenuData = [
  {
    title: 'Notismallar',
    key: 'notification-templates',
    icon: <BellIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.communication.notificationtemplates.default,
    modelName: 'notificationcontent',
  },
  {
    title: 'Utskicksmallar',
    key: 'send-out-templates',
    icon: <Icons.sendout className='mr-2 size-4' />,
    path: Urls.settings.modules.communication.sendouttemplates.default,
    modelName: 'sendout',
  },
] as const satisfies ISubMenu[];

export const presetsSubMenuData = [
  {
    title: 'Kontaktpersoner',
    key: 'sub-tenants',
    icon: <Icons.subTenant className='mr-2 size-4' />,
    path: Urls.settings.modules.presets.subtenant.default,
    modelName: 'subtenant',
  },
] as const satisfies ISubMenu[];

export const tenantPortalSubMenuData = [
  {
    title: 'Inställningar',
    key: 'customer-communication-configuration',
    path: Urls.settings.modules.tenantportal.customercommunicationconfiguration
      .default,
    icon: <UsersIcon className='mr-2 size-4' />,
    modelName: 'customercommunicationconfiguration',
  },
  {
    title: 'Hyresgästaktivering',
    key: 'automationconfiguration',
    modelName: 'automationconfiguration',
    icon: <PaperAirplaneIcon className='mr-2 size-4' />,
    path: Urls.settings.modules.tenantportal.automationconfiguration.default,
  },
  {
    title: 'Chattinställningar',
    key: 'chat',
    path: Urls.settings.modules.tenantportal.chat.chatgroupconfiguration,
  },
  {
    title: 'Hyresgästinformation',
    modelNames: ['tenantinformationcollection', 'faqentry'],
    key: 'faq',
    path: Urls.settings.modules.tenantportal.tenantinformation.default,
    modelName: 'faqentry',
  },
] as const satisfies ISubMenu[];

export const marketSubMenuItems = [
  {
    title: 'Köer',
    key: 'queues',
    path: Urls.settings.modules.market.queues.apartmentQueues,
    icon: <ListBulletIcon className='mr-2 size-4' />,
    modelName: 'apartmentqueue',
  },
  {
    title: 'Uthyrningsvillkor',
    key: 'rentable-terms',
    path: Urls.settings.modules.market.terms.apartment.overview,
    icon: <CogIcon className='mr-2 size-4' />,
    modelName: ApartmentTermsConfig.modelName,
  },
  {
    title: 'Kravprofiler',
    key: 'requirements-profiles',
    path: Urls.settings.modules.market.requirementProfiles.apartment.overview,
    icon: <CogIcon className='mr-2 size-4' />,
    modelName: 'apartmentrequirementprofile',
  },
] as const satisfies ISubMenu[];

const moduleSubMenuItems = [
  {
    title: 'Bas',
    key: 'base',
    path: '/base',
    icon: <CubeIcon className='mr-2 size-4' />,
    modelNames: getSubItemsModelNames(baseSubMenuItems),
    subMenus: baseSubMenuItems,
  },
  {
    title: 'Avisering & Fakturering',
    icon: <BanknotesIcon className='mr-2 size-4' />,
    key: 'invoicing',
    modelNames: getSubItemsModelNames(invoicingSubMenuItems),
    subMenus: invoicingSubMenuItems,
  },

  {
    title: 'Teknisk Förvaltning',
    icon: <BriefcaseIcon className='mr-2 size-4' />,
    path: '/errands',
    key: 'errands-settings',
    modelNames: getSubItemsModelNames(errandsSubMenuItems),
    subMenus: errandsSubMenuItems,
  },
  {
    title: 'Kommunikation',
    key: 'communication',
    icon: <EnvelopeIcon className='mr-2 size-4' />,
    subMenus: communicationSubMenuData,
    modelNames: getSubItemsModelNames(communicationSubMenuData),
  },
  {
    title: 'Användarmallar',
    key: 'presets',
    icon: <Icons.preset className='mr-2 size-4' />,
    subMenus: presetsSubMenuData,
    modelNames: getSubItemsModelNames(presetsSubMenuData),
  },
  {
    title: 'Mina Sidor',
    key: 'tenant-portal',
    icon: <UsersIcon className='mr-2 size-4' />,
    modelNames: getSubItemsModelNames(tenantPortalSubMenuData),
    subMenus: tenantPortalSubMenuData,
  },
  {
    title: 'Marknad & Annonsering',
    key: 'market',
    icon: <Icons.market className='mr-2 size-4' />,
    modelNames: getSubItemsModelNames(marketSubMenuItems),
    subMenus: marketSubMenuItems,
    hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET),
  },
] as const satisfies ISubMenu[];

export const errandsGeneralRouteData: ISubMenu[] = [
  {
    title: 'Ärendetyper',
    key: 'types',
    path: Urls.settings.modules.errands.general.types,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
];
export const errandsComponentRouteData: ISubMenu[] = [
  {
    title: 'Komponenttyper',
    key: 'component-types',
    path: Urls.settings.modules.errands.componentsettings.componenttypes,
    icon: <TagIcon className='mr-2 size-4' />,
  },
  {
    title: 'Komponentmodeller',
    key: 'component-models',
    path: Urls.settings.modules.errands.componentsettings.componentmodels,
    icon: <CubeIcon className='mr-2 size-4' />,
  },
  {
    title: 'Rumskategorier',
    key: 'roomcategories',
    path: Urls.settings.modules.errands.componentsettings.roomcategories,
    icon: <ArchiveBoxIcon className='mr-2 size-4' />,
  },
  {
    title: 'Komponentskick',
    key: 'component-condition-values',
    path: Urls.settings.modules.errands.componentsettings
      .componentconditionvalues,
    icon: <CheckCircleIcon className='mr-2 size-4' />,
  },
];

export const errandsExternalRouteData: ISubMenu[] = [
  {
    title: 'Leverantörer',
    key: 'suppliers',
    path: Urls.settings.modules.errands.external.suppliers,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
];

export const errandsDefaultRouteData: ISubMenu[] = [
  {
    title: 'Standardbeskrivningar',
    key: 'descriptions',
    path: Urls.settings.modules.errands.default.descriptions,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
  {
    title: 'Standardåtgärder',
    key: 'actions',
    path: Urls.settings.modules.errands.default.actions,
    icon: <WrenchScrewdriverIcon className='mr-2 size-4' />,
  },
];

export const errandInspectionsRouteData: ISubMenu[] = [
  {
    title: 'Besiktningstyper',
    key: 'types',
    path: Urls.settings.modules.errands.inspection.types,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
  {
    title: 'Protokollinställningar',
    key: 'protocolsettings',
    path: Urls.settings.modules.errands.inspection.protocolsettings,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
  {
    title: 'Egna fält',
    key: 'custom-fields',
    path: Urls.settings.modules.errands.inspection.customfields,
    icon: <BriefcaseIcon className='mr-2 size-4' />,
  },
];

export const settingsMenuData: SettingsMenu = {
  admin: {
    title: 'Admin',
    isIndex: true,
    key: 'admin',
    subMenus: adminSubMenuItems,
  },
  globalconfiguration: {
    title: 'Globala konfigurationer',
    key: 'globalConfiguration',
    subMenus: globalConfigurationsSubMenuItems,
  },
  security: {
    title: 'Säkerhet',
    key: 'security',
    subMenus: securitySubMenuItems,
  },
  modulesettings: {
    title: 'Modulinställningar',
    key: 'modulesettings',
    subMenus: moduleSubMenuItems,
  },
} as const satisfies SettingsMenu;
